import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import "./ManageEventsPage.css"; // Asegúrate de que este archivo CSS exista

function ManageEventsPage() {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState(""); // Mensajes de éxito

  // --- Estados para el pop-up de error ---
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");

  // --- Estados para Paginación ---
  const [currentPage, setCurrentPage] = useState(1);
  const [eventsPerPage] = useState(5);

  // --- Estados para Búsqueda y Filtro ---
  const [searchTerm, setSearchTerm] = useState("");
  const [searchColumn, setSearchColumn] = useState("name");

  const { token } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  // Función para obtener todos los eventos del backend
  const fetchEvents = async () => {
    setIsLoading(true);
    // Limpiar mensajes/errores de pop-up antes de cargar
    setMessage("");
    setErrorPopupMessage("");
    setShowErrorPopup(false);

    try {
      const response = await axios.get(`${API_BASE_URL}/events`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEvents(response.data);
    } catch (err) {
      // Mostrar error de carga en el pop-up si es persistente
      setErrorPopupMessage(
        err.response?.data?.error || "Error al cargar los eventos."
      );
      setShowErrorPopup(true);
      console.error("Error fetching events:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Efecto para cargar los eventos al montar el componente o cuando el token cambia
  useEffect(() => {
    if (token) {
      fetchEvents();
    } else {
      setErrorPopupMessage("No autenticado. Por favor, inicie sesión.");
      setShowErrorPopup(true);
      setIsLoading(false);
    }
  }, [token, API_BASE_URL]);

  // --- Lógica de Filtrado en Tiempo Real ---
  const filteredEvents = useMemo(() => {
    if (!searchTerm) {
      return events;
    }
    return events.filter((event) => {
      const value = String(event[searchColumn]).toLowerCase();
      return value.includes(searchTerm.toLowerCase());
    });
  }, [events, searchTerm, searchColumn]);

  // --- Lógica de Paginación ---
  const indexOfLastEvent = currentPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = filteredEvents.slice(
    indexOfFirstEvent,
    indexOfLastEvent
  );

  const totalPages = Math.ceil(filteredEvents.length / eventsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Función para editar un evento
  const handleEditEvent = (eventId) => {
    navigate(`/admin/events/edit/${eventId}`);
  };

  // Función para eliminar un evento
  const handleDeleteEvent = async (eventId) => {
    if (
      !window.confirm(
        "¿Está seguro de que desea eliminar este evento? Esta acción no se puede deshacer."
      )
    ) {
      return;
    }
    setMessage("");
    setErrorPopupMessage(""); // Limpiar mensaje de error del pop-up
    setShowErrorPopup(false); // Ocultar pop-up
    setIsLoading(true);

    try {
      const response = await axios.delete(`${API_BASE_URL}/events/${eventId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMessage(response.data.message || "Evento eliminado exitosamente.");
      fetchEvents(); // Recargar eventos
    } catch (err) {
      // Mostrar error en el pop-up
      setErrorPopupMessage(
        err.response?.data?.error || "Error al eliminar el evento."
      );
      setShowErrorPopup(true); // Mostrar pop-up de error
      console.error("Error deleting event:", err.response?.data || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para cerrar el pop-up de error
  const handleCloseErrorPopup = () => {
    setShowErrorPopup(false);
    setErrorPopupMessage("");
    // No redirigimos, nos quedamos en la misma página de gestión de eventos
  };

  if (isLoading) {
    return (
      <div className="manage-events-container">
        <p>Cargando eventos...</p>
      </div>
    );
  }

  // No mostramos un div de error que ocupe toda la pantalla, el pop-up lo maneja
  // if (error) { ... }

  // Función para descargar plantilla Excel
  const handleDownloadTemplate = async (eventId, eventName) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/events/${eventId}/download-template-excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob", // muy importante para recibir archivo
        }
      );

      // Crear blob y descargarlo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      // Nombre dinámico: plantilla-NombreEvento.xlsx
      link.setAttribute("download", `plantilla-${eventName}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setErrorPopupMessage(
        err.response?.data?.error || "Error al descargar la plantilla."
      );
      setShowErrorPopup(true);
      console.error("Error downloading template:", err);
    }
  };

  // Devuelve un array de páginas a mostrar en la paginación (máximo 7)
  const getVisiblePages = () => {
    const maxVisible = 7;
    let start = Math.max(currentPage - Math.floor(maxVisible / 2), 1);
    let end = start + maxVisible - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxVisible + 1, 1);
    }

    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="manage-events-container">
      <div className="manage-events-card">
        <h2 className="manage-events-title">Gestión de Eventos</h2>
        <p className="manage-events-description">
          Administra los eventos para los cuales se emiten certificados.
        </p>

        <div className="top-controls">
          <Link to="/admin/events/create" className="create-event-button">
            <span className="material-icons">add</span> Crear Nuevo Evento
          </Link>
          <div className="search-filter-container">
            <select
              value={searchColumn}
              onChange={(e) => setSearchColumn(e.target.value)}
              className="search-column-select"
            >
              <option value="name">Nombre del Evento</option>
              <option value="organizer_name">Organizador</option>
            </select>
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />
          </div>
        </div>

        {message && <p className="manage-events-message success">{message}</p>}
        {/* No mostramos el error aquí, se mostrará en el pop-up */}
        {/* {error && <p className="manage-events-message error">{error}</p>} */}

        {filteredEvents.length === 0 && searchTerm !== "" ? (
          <p className="no-results-message">
            No se encontraron eventos que coincidan con la búsqueda.
          </p>
        ) : filteredEvents.length === 0 ? (
          <p className="no-results-message">
            No hay eventos registrados.{" "}
            <Link to="/admin/events/create">Crea uno ahora</Link>.
          </p>
        ) : (
          <table className="events-table">
            <thead>
              <tr>
                <th>Nombre del Evento</th>
                <th>Organizador</th>
                <th>Tipo de Evento</th>
                <th>Horas</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentEvents.map((event) => (
                <tr key={event.id}>
                  <td>{event.name}</td>
                  <td>{event.organizer_name}</td>
                  <td>{event.event_type_name}</td>
                  <td>{event.hours_quantity || "N/A"}</td>
                  <td>
                    <div className="action-buttons-container">
                      <button
                        onClick={() => handleEditEvent(event.id)}
                        className="action-button edit-button"
                        disabled={isLoading}
                      >
                        <span className="material-icons">edit</span>
                        <span className="button-text">Editar</span>
                      </button>
                      <button
                        onClick={() => handleDeleteEvent(event.id)}
                        className="action-button delete-button"
                        disabled={isLoading}
                      >
                        <span className="material-icons">delete</span>
                        <span className="button-text">Eliminar</span>
                      </button>
                      <button
                        onClick={() =>
                          handleDownloadTemplate(event.id, event.name)
                        }
                        className="action-button download-button"
                        disabled={isLoading}
                      >
                        <span className="material-icons">download</span>
                        <span className="button-text">Plantilla</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Controles de Paginación */}
        {filteredEvents.length > eventsPerPage && (
          <div className="pagination-controls">
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Anterior
            </button>

            {getVisiblePages().map((page) => (
              <button
                key={page}
                onClick={() => paginate(page)}
                className={`pagination-button ${
                  currentPage === page ? "active" : ""
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {/* Pop-up de Error */}
      {showErrorPopup && (
        <div className="popup-overlay">
          <div className="popup-content error-popup">
            {" "}
            {/* Añadir clase para estilos de error */}
            <h3>¡Error!</h3>
            <p>{errorPopupMessage}</p>
            <button
              onClick={handleCloseErrorPopup}
              className="popup-button error-button"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageEventsPage;
